import { createRoot } from 'react-dom/client';
import './styles.css';

const navLinks = [
  ['Install', '#install'],
  ['Surfaces', '#surfaces'],
  ['Browsers', '#browsers'],
  ['Roadmap', '#roadmap'],
] as const;

const metaPills = [
  'LIVE ON CHROME / FIREFOX / EDGE',
  'GMAIL / ZOHO',
  'MICROSOFT / ICLOUD / QQ / NETEASE',
] as const;

const storeLinks = [
  {
    name: 'Chrome Web Store',
    href: 'https://chromewebstore.google.com/detail/unimail-%E2%80%94-unified-inbox/ndakahpnkhidhhlmlfchjdehpdegaabd',
    body: 'Install UniMail for Chrome and Chromium-based daily browsing.',
    icon: <img src="/icons/chrome.png" alt="" />,
  },
  {
    name: 'Firefox Add-ons',
    href: 'https://addons.mozilla.org/zh-CN/firefox/addon/unimail-unified-inbox/',
    body: 'Install UniMail from Mozilla Add-ons for Firefox.',
    icon: <img src="/icons/firefox.svg" alt="" />,
  },
  {
    name: 'Microsoft Edge Add-ons',
    href: 'https://microsoftedge.microsoft.com/addons/detail/unimail-%E2%80%94-unified-inbox/hmgipgiddjihijbbglbhnnlbhghflndo',
    body: 'Install UniMail from Microsoft Edge Add-ons for Edge.',
    icon: <img src="/icons/edge.png" alt="" />,
  },
] as const;

const surfaces = [
  {
    title: 'Popup: list, detail, reply.',
    body: 'Open UniMail, read the active message, reply without leaving the current tab.',
    image: '/screenshots/unimail-popup-detail.png',
    alt: 'UniMail popup screenshot',
    narrow: false,
  },
  {
    title: 'SidePanel: inbox that stays.',
    body: 'Keep unread mail visible beside any webpage, with provider badges and message previews.',
    image: '/screenshots/unimail-sidepanel-list.jpg',
    alt: 'UniMail side panel screenshot',
    narrow: true,
  },
] as const;

const browsers = [
  {
    name: 'Chrome',
    body: 'Available now on the Chrome Web Store.',
    icon: <img src="/icons/chrome.png" alt="" />,
    href: storeLinks[0].href,
  },
  {
    name: 'Firefox',
    body: 'Available now on Mozilla Add-ons.',
    icon: <img src="/icons/firefox.svg" alt="" />,
    href: storeLinks[1].href,
  },
  {
    name: 'Edge',
    body: 'Available now on Microsoft Edge Add-ons.',
    icon: <img src="/icons/edge.png" alt="" />,
    href: storeLinks[2].href,
  },
] as const;

const providers = [
  ['NOW', 'Gmail / Zoho', 'The first mailbox pair for a clean daily workflow.'],
  ['NEXT', 'Microsoft / iCloud', 'Planned expansion for work accounts and Apple mail users.'],
  ['LATER', 'QQ / NetEase', 'Planned support for common Chinese mailboxes and more services.'],
] as const;

const PAYPAL_URL = 'https://www.paypal.com/paypalme/yeomanye';

function PayPalIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.067 8.478c.492.315.844.825.983 1.39.426 1.738-.505 3.723-2.138 4.535-.546.274-1.168.424-1.792.424H15.9l-.483 2.897H13.03l1.587-9.483h3.614c.7 0 1.36.1 1.836.237zm-5.353 4.673h1.023c.452 0 .866-.097 1.198-.273.612-.319.982-1.003.802-1.724-.09-.36-.31-.621-.627-.785-.268-.136-.607-.204-.963-.204h-.905l-.528 2.986zM9.703 8.478c.492.315.844.825.983 1.39.426 1.738-.505 3.723-2.138 4.535-.546.274-1.168.424-1.792.424H5.536l-.483 2.897H2.666l1.587-9.483h3.614c.7 0 1.36.1 1.836.237zm-5.353 4.673h1.023c.452 0 .866-.097 1.198-.273.612-.319.982-1.003.802-1.724-.09-.36-.31-.621-.627-.785-.268-.136-.607-.204-.963-.204H4.878l-.528 2.986z" />
    </svg>
  );
}

function Brand() {
  return (
    <a className="brand" href="#top" aria-label="UniMail">
      <img className="brand-icon" src="/icons/unimail-icon.png" alt="" aria-hidden="true" />
      <span>UniMail</span>
    </a>
  );
}

function NavBar() {
  return (
    <nav className="nav" aria-label="Page navigation">
      <Brand />
      <div className="nav-links">
        {navLinks.map(([label, href]) => (
          <a key={label} href={href}>
            {label}
          </a>
        ))}
      </div>
    </nav>
  );
}

function ProductShots() {
  return (
    <div className="hero-shots" aria-label="UniMail current product screenshots">
      <img
        className="shot popup-shot"
        src="/screenshots/unimail-popup-detail.png"
        alt="UniMail popup mail detail"
      />
      <img
        className="shot side-shot"
        src="/screenshots/unimail-sidepanel-list.jpg"
        alt="UniMail side panel mail list"
      />
    </div>
  );
}

function MetaRow() {
  return (
    <div className="meta-row" aria-label="Product summary">
      {metaPills.map((pill) => (
        <span className="pill" key={pill}>
          {pill}
        </span>
      ))}
    </div>
  );
}

function HeroSection() {
  return (
    <section className="hero" id="top">
      <div className="hero-copy">
        <p className="kicker">UNIMAIL / SIMPLE BROWSER INBOX</p>
        <h1>A simple inbox in your browser.</h1>
        <p className="lead">
          Convenient, easy to use, polished, and comfortable. UniMail is now available on Chrome,
          Firefox, and Edge, starting with Gmail and Zoho.
        </p>
        <div className="actions">
          <a
            className="button primary"
            href={storeLinks[0].href}
            target="_blank"
            rel="noopener noreferrer"
          >
            Install for Chrome
          </a>
          <a className="button secondary" href="#install">
            Other browsers
          </a>
        </div>
      </div>
      <ProductShots />
      <div className="hero-stamp" aria-hidden="true">
        01
      </div>
      <MetaRow />
    </section>
  );
}

function SectionHeader({ kicker, title, body }: { kicker: string; title: string; body: string }) {
  return (
    <div className="section-head">
      <div>
        <p className="kicker">{kicker}</p>
        <h2>{title}</h2>
      </div>
      <p>{body}</p>
    </div>
  );
}

function SurfaceSection() {
  return (
    <section className="section" id="surfaces">
      <SectionHeader
        kicker="EASY EVERYDAY MAIL"
        title="Read, scan, and reply without breaking flow."
        body="UniMail keeps common mail moments close to the page you are already viewing: light, readable, and comfortable enough to leave open."
      />
      <div className="surface-grid">
        {surfaces.map((surface) => (
          <article className={`surface-card${surface.narrow ? ' narrow' : ''}`} key={surface.title}>
            <div>
              <h3>{surface.title}</h3>
              <p>{surface.body}</p>
            </div>
            <img src={surface.image} alt={surface.alt} />
          </article>
        ))}
      </div>
    </section>
  );
}

function BrowserSection() {
  return (
    <section className="section" id="browsers">
      <SectionHeader
        kicker="BROWSER SUPPORT"
        title="Install UniMail from your browser's store."
        body="UniMail is listed on Chrome Web Store, Mozilla Add-ons, and Microsoft Edge Add-ons."
      />
      <div className="browser-grid">
        {browsers.map((browser) => (
          <a
            className="browser-card"
            href={browser.href}
            target="_blank"
            rel="noopener noreferrer"
            key={browser.name}
            aria-label={`Install UniMail for ${browser.name}`}
          >
            {browser.icon}
            <div>
              <h3>{browser.name}</h3>
              <p>{browser.body}</p>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function InstallSection() {
  return (
    <section className="section install-section" id="install">
      <SectionHeader
        kicker="AVAILABLE NOW"
        title="Choose your browser store."
        body="Use the official listing for your browser. Each store page includes the current extension package, screenshots, and privacy disclosures."
      />
      <div className="store-grid">
        {storeLinks.map((store) => (
          <a
            className="store-card"
            href={store.href}
            target="_blank"
            rel="noopener noreferrer"
            key={store.name}
            aria-label={`Install UniMail from ${store.name}`}
          >
            <span className="store-icon">{store.icon}</span>
            <span>
              <strong>{store.name}</strong>
              <small>{store.body}</small>
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}

function RoadmapSection() {
  return (
    <section className="section" id="roadmap">
      <SectionHeader
        kicker="MAILBOX ROADMAP"
        title="From Gmail and Zoho to the mailboxes people already have."
        body="The outline is simple: start with a small, polished inbox, then expand to Microsoft, iCloud, QQ Mail, NetEase Mail, and more regional services."
      />
      <div className="providers">
        {providers.map(([stage, title, body]) => (
          <article className="provider-card" key={stage}>
            <b>{stage}</b>
            <h3>{title}</h3>
            <p>{body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="section">
      <div className="cta">
        <p className="kicker">INSTALL / READ / REPLY</p>
        <h2>A browser inbox that feels light enough to keep close.</h2>
        <div className="actions">
          <a
            className="button primary"
            href={storeLinks[0].href}
            target="_blank"
            rel="noopener noreferrer"
          >
            Chrome Web Store
          </a>
          <a className="button secondary" href="#install">
            Firefox / Edge
          </a>
        </div>
      </div>
    </section>
  );
}

function DonateSection() {
  return (
    <section className="section donate-section" id="donate">
      <div className="donate-card">
        <div>
          <p className="kicker">SUPPORT / PAYPAL</p>
          <h2>Support a small browser inbox project.</h2>
        </div>
        <div className="donate-copy">
          <p>
            UniMail is built as an independent tool for a simpler, more comfortable inbox workflow.
            If it saves you time, a small PayPal donation helps keep development moving.
          </p>
          <a
            className="donate-button"
            href={PAYPAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Support UniMail via PayPal"
          >
            <PayPalIcon />
            <span>Donate via PayPal</span>
          </a>
        </div>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="footer">
      <span>UNIMAIL</span>
      <span>/</span>
      <span>SIMPLE BROWSER INBOX</span>
      <span>/</span>
      <span>2026</span>
      <span>/</span>
      <a href={storeLinks[0].href} target="_blank" rel="noopener noreferrer">
        CHROME
      </a>
      <span>/</span>
      <a href={storeLinks[1].href} target="_blank" rel="noopener noreferrer">
        FIREFOX
      </a>
      <span>/</span>
      <a href={storeLinks[2].href} target="_blank" rel="noopener noreferrer">
        EDGE
      </a>
      <span>/</span>
      <a href="/privacy.html">PRIVACY</a>
      <span>/</span>
      <a href={PAYPAL_URL} target="_blank" rel="noopener noreferrer">
        DONATE
      </a>
    </footer>
  );
}

function App() {
  return (
    <main className="page">
      <NavBar />
      <HeroSection />
      <InstallSection />
      <SurfaceSection />
      <BrowserSection />
      <RoadmapSection />
      <CtaSection />
      <DonateSection />
      <SiteFooter />
    </main>
  );
}

const container = document.getElementById('root');
if (!container) throw new Error('#root not found');

createRoot(container).render(<App />);
